<template>
    <div class="relative-container" ref="rootEl">
        <div class="labels-header">
            <img class="labels-buttons" src="../assets//guidelines.png" alt="upload" title="Project Guidelines" @mouseenter="onEnterPreview" @mousemove="updatePopupPosition" @mouseleave="onLeavePreview" :class="{ 'disabled-image': !projectImageReady }">
            <div v-if="showPreview && projectImageReady" class="image-preview-popup" :style="{ top: popupPosition.top + 'px', left: popupPosition.left + 'px' }">
                <img :src="projectImageUrl" class="image-preview" alt="Project Preview" :style="{ width: originalImageSize.width + 'px', height: originalImageSize.height + 'px' }" />
            </div>
        </div>
        <div class="labels-content" :class="{ empty: lines.length === 0 }">
            <div v-if="lines.length > 0">
                <div v-for="(line, index) in lines" :key="line.id" class="label-row">
                    <label class="sr-only" :for="`label-${line.id}`">Label for line {{ index + 1 }}</label>

                    <input
                        class="item-input"
                        :class="{ 'label-hidden': line.is_hidden }"
                        :id="`label-${line.id}`"
                        :name="`label-${line.id}`"
                        :data-line-id="line.id"
                        :readonly="readOnly"
                        type="text"
                        v-model="line.label"
                        @click="onFocus(line)"
                        @input="!readOnly && onInput(line)"
                        @keyup.enter.prevent="!readOnly && onEnter(line, index)"
                        @keyup.esc="!readOnly && onEsc($event, line)"
                        @focus="onFocus(line)"
                        @blur="onBlur(line)"
                    />

                    <div class="label-actions">
                        <button v-if="isEditorMode && !readOnly && focusedId === line.id" class="label-trash" title="Delete label"
                                @mousedown.prevent @click.stop="onDelete(line)" aria-label="Delete label"tabindex="-1">
                            <font-awesome-icon icon="trash-can" />
                        </button>

                        <button v-if="isEditorMode && focusedId === line.id" class="label-eye" :title="line.is_hidden ? 'Show' : 'Hide'"
                                @mousedown.prevent @click.stop="toggleHidden(line)" aria-label="Toggle visibility" tabindex="-1">
                            <font-awesome-icon :icon="line.is_hidden ? 'eye-slash' : 'eye'" />
                        </button>

                        <button v-if="isReviewMode" type="button" class="label-action-btn" :class="{ active: wrongIdsSet.has(line.id) }" title="Tag as Wrong" @click.stop="$emit('markWrong', line)" aria-label="Tag Wrong" tabindex="-1">
                            <img src="../assets/invalidate.png" alt="" />
                        </button>

                        <div v-else-if="isEditorMode && line.review_wrong" class="label-wrong-wrap">
                            <img class="wrong-icon" src="../assets/alert.png" alt="" title="Line was considered wrong" @click.stop="openSolveMenu(line)"/>
                            <div v-if="solveMenuFor === line.id" class="solve-menu" role="menu">
                                <button type="button" @click="solve(line)" style="color: white;">Solved</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="icon-container">
                <div class="icon-text-container">
                    <span>No Labels</span>
                </div>
            </div>
        </div>        
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted, onBeforeUnmount, nextTick, computed } from "vue";
import type { PropType } from "vue";
import { Line, ImageInfo } from "../helpers/Interfaces"
import ProjectService from "@/services/Project";
import { subscriptionManager } from "@/realtime/SubscriptionManager";

export default defineComponent({
    name: "LabelsComponent",
    props: {
        image: {
            type: Object as PropType<ImageInfo | null>,
            required: true
        },
        onNewLine: {
            type: null as unknown as PropType<Line | null>,
            default: null,
        },
        onDeleteLine: {
            type: null as unknown as PropType<Line | null>,
            default: null,
        },
        readOnly: {
            type: Boolean, default: false
        },
        wrongIds: {
             type: Array as PropType<number[]>, default: () => []
        },
        mode: { type: String as PropType<'review' | 'editor'>, default: 'editor' },
    },
    emits: ["onLabelFocus", "onLabelChanged", "markWrong"],
    setup(props, {emit, expose }) {
        const rootEl = ref<HTMLElement|null>(null);
        const lines = ref<Line[]>([]);
        const showPreview = ref(false);
        const projectImageUrl = ref('');
        const originalImageSize = ref({ width: 0, height: 0 });
        const popupPosition = ref({ top: 0, left: 0 });
        const projectImageReady = ref(false);

        const toLabelString = (val: unknown): string =>
            typeof val === "string" ? val : val == null ? "" : String(val);

        const trimmed = (line: Line) => toLabelString(line.label).trim();

        const pendingTimers = new Map<number, number>();
        const lastSavedLabel = new Map<number, string>();
        const originalOnFocus = new Map<number, string>();

        const solveMenuFor = ref<number | null>(null);
        const wrongIdsSet = computed(() => new Set(props.wrongIds ?? []));
        const isReviewMode = computed(() => props.mode === 'review');
        const isEditorMode = computed(() => props.mode === 'editor');        
        const focusedId = ref<number | null>(null);

        let annoUnsubs: Array<() => void> = [];

        watch(() => props.image, async (img, old) => {
            if (old?.id) await flushAllPending();
            await offAnnoSubs();

            if (!img?.id) {
                lines.value = [];
                return;
            }
            await loadImageAnnotations(img.id);
            await subAnnotations(img.id);
        }, { immediate: true });

        watch(() => props.onNewLine, (nl) => {
            if (!nl) return;
            if (!lines.value.some(l => l.id === nl.id)) {
                const copy = { ...nl, label: toLabelString(nl.label), is_hidden: !!(nl as any).is_hidden };
                lines.value.push(copy);
                lastSavedLabel.set(copy.id, trimmed(copy));
            }
        }, { deep: true });

        watch(() => props.onDeleteLine, (ol) => {
            if (!ol) return;
            clearTimeout(pendingTimers.get(ol.id));
            pendingTimers.delete(ol.id);
            lastSavedLabel.delete(ol.id);
            lines.value = lines.value.filter(l => l.id !== ol.id);
        });

        watch(() => props.image?.project_id, async (newProjectId) => {
                if (newProjectId) {
                    projectImageUrl.value = await ProjectService.getProjectImageUrl(newProjectId);
                } else {
                    projectImageUrl.value = '';
                }
        }, { immediate: true });

        watch(projectImageUrl, async (newUrl) => {
            projectImageReady.value = false;
            originalImageSize.value = { width: 0, height: 0 };
            if (!newUrl) return;

            const img = new Image();
            img.onload = () => {
                originalImageSize.value = {
                width: (img as any).naturalWidth || img.width,
                height: (img as any).naturalHeight || img.height
                };
                projectImageReady.value = true;
            };
            img.onerror = () => {
                projectImageReady.value = false; // Failed... will not show image
            };
            img.src = newUrl;
        });

        function updatePopupPosition(event: MouseEvent) {
            const previewWidth = originalImageSize.value.width;
            const previewHeight = originalImageSize.value.height;

            const padding = 10;
            const { clientX, clientY } = event;
            const { innerWidth, innerHeight } = window;

            let left = clientX + padding;
            let top = clientY + padding;

            if (left + previewWidth > innerWidth) {
                left = innerWidth - previewWidth - padding;
            }

            if (top + previewHeight > innerHeight) {
                top = innerHeight - previewHeight - padding;
            }

            popupPosition.value = { top, left };
        }

        async function loadImageAnnotations(imageId: number) {
            lines.value = await ProjectService.getAnnotationsForImage(imageId) || [];
            lastSavedLabel.clear();
            for (const l of lines.value) lastSavedLabel.set(l.id, trimmed(l));
        }        

        async function offAnnoSubs() {
            try { annoUnsubs.forEach(off => off()); } catch {}
            annoUnsubs = [];
        }

        async function subAnnotations(imageId: number) {
            await offAnnoSubs();
            const topic = subscriptionManager.topicAnnotations(imageId);

            // INSERT
            annoUnsubs.push(await subscriptionManager.subscribe(topic, "annotations:insert", ({ line }) => {
            if (!lines.value.some(l => l.id === line.id)) {
                lines.value.push(line);
                lastSavedLabel.set(line.id, trimmed(line));
            }
            }));

            // UPDATE (não sobrescreve enquanto o input dessa linha estiver focado)
            annoUnsubs.push(await subscriptionManager.subscribe(topic, "annotations:update", ({ line }) => {
            const active = document.activeElement as HTMLElement | null;
            const activeId = Number(active?.getAttribute?.('data-line-id') || NaN);
            if (activeId === line.id) return;

            const idx = lines.value.findIndex(l => l.id === line.id);
            if (idx >= 0) {
                lines.value.splice(idx, 1, line);
                lastSavedLabel.set(line.id, trimmed(line));
            } else {
                lines.value.push(line);
                lastSavedLabel.set(line.id, trimmed(line));
            }
            }));

            // DELETE
            annoUnsubs.push(await subscriptionManager.subscribe(topic, "annotations:delete", ({ id }) => {
            lines.value = lines.value.filter(l => l.id !== id);
            pendingTimers.delete(id);
            lastSavedLabel.delete(id);
            }));
        }

        async function toggleHidden(line: Line) {
            if (!props.image?.id || !line?.id) return;

            line.is_hidden = !line.is_hidden;

            try {
                await ProjectService.setAnnotationHidden(props.image.id, line.id, !!line.is_hidden);
            } catch (e) {
                console.error('Falha ao alterar visibilidade:', e);
                // rollback
                line.is_hidden = !line.is_hidden;
                alert('Falha ao alterar visibilidade.');
            }
        }

        function onAnnotationChanged(event: string, updatedLine?: Line, deletedId?: number) {
            console.log(event);
            if (event === "insert" && updatedLine) {
                if (!lines.value.some(line => line.id === updatedLine.id)) {
                    lines.value.push(updatedLine);
                }
            } else if (event === "update" && updatedLine) {
                lines.value = lines.value.map(line => (line.id === updatedLine.id ? updatedLine : line));
            } else if (event === "delete" && deletedId) {
                console.log(deletedId);
                lines.value = lines.value.filter(line => line.id !== deletedId);
            }
        }

        function onEnterPreview(event: MouseEvent) {
            if (!projectImageUrl.value || !projectImageReady.value) return;
            updatePopupPosition(event);
            showPreview.value = true;
        }

        function onLeavePreview() {
            showPreview.value = false;
        }

        async function onEnter(line: Line, index: number) {
            await flushOne(line);

            const nextLine = lines.value[index + 1];

            if (!nextLine) {
                const active = document.activeElement as HTMLInputElement | null;
                active?.blur();
                emit("onLabelFocus", null);
                return;
            }

            await nextTick();
            const inputs = Array.from(document.querySelectorAll('.relative-container input.item-input'));
            const next = inputs[index + 1] as HTMLInputElement | undefined;
            if (next) {
                next.focus();
                next.select();
            }

            // reforça o highlight da próxima linha (ganha à corrida do blur)
            emit("onLabelFocus", nextLine);
        }

        function onEsc(ev: KeyboardEvent, line: Line)  {
            ev.preventDefault();
            const original = originalOnFocus.get(line.id);
            if (original !== undefined && original !== toLabelString(line.label)) {
                line.label = original;
            }
            (ev.target as HTMLInputElement)?.blur();
        }

        function onInput(line: Line) {
            line.label = toLabelString(line.label);
            scheduleSave(line);
        }

        function onFocus(line: Line) {
            focusedId.value = line.id;
            originalOnFocus.set(line.id, toLabelString(line.label));
            if (line) emit("onLabelFocus", line);
        }

        async function onBlur(line: Line) {
            await flushOne(line);

            setTimeout(() => {
                const active = document.activeElement as HTMLElement | null;

                if (!active || !active.classList.contains('item-input')) {
                    focusedId.value = null;
                    emit("onLabelFocus", null);
                    return;
                }

                const idAttr = active.getAttribute('data-line-id');
                const activeId = idAttr ? Number(idAttr) : NaN;
                const focusedLine = lines.value.find(l => l.id === activeId);
                if (focusedLine) {
                    emit("onLabelFocus", focusedLine);
                } else {
                    emit("onLabelFocus", null);
                }
            }, 0);
        }        

        async function onDelete(line: Line) {
            try {
                if (!props.image?.id || !line?.id) return;

                clearTimeout(pendingTimers.get(line.id));
                pendingTimers.delete(line.id);

                console.log('[Labels] deleting annotation', { imageId: props.image.id, lineId: line.id });
                await ProjectService.deleteAnnotation(props.image.id, line.id);
                console.log('[Labels] delete ok');

                // UI local imediata
                lines.value = lines.value.filter(l => l.id !== line.id);
                lastSavedLabel.delete(line.id);
                if (focusedId.value === line.id) focusedId.value = null;
                emit("onLabelFocus", null);
            } catch (e) {
                console.error('Falha a apagar linha:', e);
                alert('Falha a apagar a linha.');
            }
        }

        function scheduleSave(line: Line, delay = 400) {
            if (!props.image) return;
            const current = trimmed(line);
            if (current === lastSavedLabel.get(line.id)) return;
            clearTimeout(pendingTimers.get(line.id));
            const t = window.setTimeout(() => flushOne(line), delay);
            pendingTimers.set(line.id, t);
        }

        async function flushOne(line: Line) {
            if (!props.image) return;
            clearTimeout(pendingTimers.get(line.id));
            pendingTimers.delete(line.id);
            const current = trimmed(line);
            if (current === lastSavedLabel.get(line.id)) return;
            
            emit("onLabelChanged", line.id, lastSavedLabel.get(line.id) || "", current);
            lastSavedLabel.set(line.id, current);
        }

        async function flushAllPending() {
            const toFlush = lines.value.filter(l => pendingTimers.has(l.id));
            for (const l of toFlush) await flushOne(l);
        }

        function moveToNextInput(index: number) {
            const inputs = Array.from(document.querySelectorAll('.relative-container input.item-input'));
            const next = inputs[index + 1] as HTMLInputElement | undefined;
            if (next) { 
                next.focus(); next.select(); 
            }
        }

        function openSolveMenu(line: Line) {
            solveMenuFor.value = line.id;
            const onDoc = (e: MouseEvent) => {
                const el = e.target as HTMLElement;
                if (!el.closest('.label-wrong-wrap')) {
                    solveMenuFor.value = null;
                    document.removeEventListener('click', onDoc);
                }
            };
            document.addEventListener('click', onDoc);
        }

        async function solve(line: Line) {
            if (!props.image?.id || !line?.id) return;
            try {
                await ProjectService.clearAnnotationWrong(props.image.id, line.id);
                // atualização imediata no Editor
                line.review_wrong = false;
                line.review_notes = null;
            } catch (e) {
                console.error('Falha a limpar wrong:', e);
                alert('Falha a limpar estado “errado”.');
            } finally {
                solveMenuFor.value = null;
            }
        }

        function shakeWrong() {
            // abana cada ícone de “errado”
            const nodes = rootEl.value?.querySelectorAll('.label-wrong-wrap') ?? [];
            nodes.forEach((n) => {
                n.classList.remove('shake'); // reinicia se já estava
                // força reflow para reiniciar a animação
                // @ts-ignore
                void (n as HTMLElement).offsetWidth;
                n.classList.add('shake');
                const onEnd = () => (n as HTMLElement).classList.remove('shake');
                n.addEventListener('animationend', onEnd, { once: true });
            });
        }

        expose({ shakeWrong });

        onMounted(async () => {

        });

        onBeforeUnmount(async () => {
            await flushAllPending();
            await offAnnoSubs();
        });

        return {
            rootEl, lines, onEnter, onEsc, onFocus, onBlur, onInput, showPreview, projectImageUrl, originalImageSize, popupPosition,
            updatePopupPosition, projectImageReady, onEnterPreview, onLeavePreview, openSolveMenu, solve, wrongIdsSet, solveMenuFor,
            isReviewMode, isEditorMode, focusedId, onDelete, toggleHidden }
    }
});
</script>

<style scoped>
.relative-container {
    background-color: rgb(36, 36, 36);
    border: 2px solid rgb(51, 51, 51);
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    position: relative;
    padding-top: 10px;
    flex-direction: column;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: grey transparent;    
}

.labels-header {
    flex-shrink: 0;
    width: 100%;
    height: 30px;
    background-color: rgb(56, 56, 56);
    display: flex;
    align-items: center;
    justify-content: start;
    z-index: 1;
    border-bottom: 1px solid #9e9e9e;
}

.labels-content {
    flex-grow: 1;
    flex-shrink: 1;
    min-height: 0;
    overflow-y: auto;
    position: relative;
    scrollbar-gutter: stable; /* Helps prevent content shift when scrollbar appears */
    padding-right: 10px; /* Space for scrollbar */
    scrollbar-width: thin;          /* "auto" or "thin" */
    scrollbar-color: rgb(80, 80, 80) transparent;   /* scroll thumb and track */ 
    overflow: overlay;
}

.labels-buttons {
    padding: 3px;
    width: 20px;
    height: auto;
    margin-left: 10px;
    border: 2px solid transparent;
    margin-left: 10px;
}

.labels-content.empty {
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-container {
    font-size: xx-large;
    width: 100%;
    text-align: center;
}

.icon-text-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: #616161;
    font-family: Arial, Helvetica, sans-serif;
    font-weight: bold;
}

.item-input {
    background-color: rgb(36, 36, 36);
    color: #969696;
    width: 100%;
    align-items: center;
    height: 25px;
    border: none;
    border-bottom: 1px solid #969696;
    margin-bottom: 5px;
}

.item-input:hover {
    background-color: grey;
    color: white;
}

.item-input:focus {
    background-color: grey;
    outline: none;
}

.item-input::selection {
  color: grey; /* Cor do texto selecionado */
  background: white; /* Cor de fundo do texto selecionado */
}

.image-preview-popup {
  position: fixed;
  background-color: #222;
  border: 1px solid #999;
  padding: 5px;
  z-index: 999;
  box-shadow: 0 0 10px rgba(0,0,0,0.5);
}

.image-preview {
  display: block;
}

.disabled-image {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.sr-only {
  position: absolute !important;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0,0,0,0);
  white-space: nowrap; border: 0;
}

.label-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-input {
  flex: 1;
}

.label-actions {
  display: flex;
  align-items: center;
  position: relative;
}

.label-action-btn {
  border: 1px solid #4d4d4d;
  background: #2b2b2b;
  border-radius: 6px;
  padding: 2px 4px;
  width: 26px; height: 26px;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer;
  opacity: .9;
}
.label-action-btn.active { box-shadow: 0 0 0 2px rgba(239,68,68,.45) inset; }

.label-action-btn img,
.wrong-icon { width: 18px; height: 18px; }

.label-wrong-wrap { position: relative; }

.solve-menu {
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 6px;
  background: #333;
  border: 1px solid #555;
  border-radius: 6px;
  padding: 6px;
  box-shadow: 0 6px 16px rgba(0,0,0,.35);
  z-index: 50;
}

.solve-menu button {
  background: #2b2b2b;
  border: 1px solid #4d4d4d;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
}

@keyframes nudge-shake {
  0%, 100% { transform: translateX(0); }
  15% { transform: translateX(-4px); }
  30% { transform: translateX(4px); }
  45% { transform: translateX(-3px); }
  60% { transform: translateX(3px); }
  75% { transform: translateX(-2px); }
  90% { transform: translateX(2px); }
}
.label-wrong-wrap.shake {
  animation: nudge-shake 480ms ease;
}

.label-trash {
  background: transparent;
  border: none;
  padding: 0;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #9aa0a6;
  cursor: pointer;
}

.label-eye {
  background: transparent;
  border: none;
  padding: 0;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #9aa0a6;
  cursor: pointer;
}

.label-trash:hover {
  color: #ffffff;
}

.label-trash:focus {
  outline: none;
}

.label-trash :deep(svg) {
  width: 18px;
  height: 18px;
}

.label-hidden {
  color: #e53935;
  opacity: 0.85;
}
</style>